import { computed, unref } from '@vue/composition-api'

export default (options = {}) => {
  // 1. オプション設定のデフォルト値
  const {
    showTertiaryColumns = false,
    includeActions = false, // actions列を含むか（固定列）
    includeDate = false, // 日付列を含むか
    useColumnWidths = false, // 列幅設定を使用するか
    useLabelIds = true, // true: expensePrimaryLabelId, false: expensePrimaryLabelName
  } = options

  // 2. 列幅設定の定数定義
  const COLUMN_WIDTHS = {
    ACTIONS: { maxWidth: '100px', minWidth: '100px', width: '100px' },
    DATE: { maxWidth: '120px', minWidth: '120px', width: '120px' },
    LABEL: { maxWidth: '250px', minWidth: '250px', width: '250px' },
    STANDARD: { maxWidth: '160px', minWidth: '160px', width: '160px' },
  }

  // 3. 固定列設定のヘルパー関数
  const createFixedColumn = config => ({
    ...config,
    class: 'fixed-cell',
    cellClass: 'fixed-cell',
  })

  // 4. 列幅を適用するヘルパー関数
  const applyWidth = (header, widthConfig) => {
    if (!useColumnWidths) {
      return header
    }

    return { ...header, ...widthConfig }
  }

  // 5. ヘッダー構築関数
  const buildExpenseHeaders = () => {
    const headers = []
    // showTertiaryColumns を reactive な値として取得（ref または computed の場合に対応）
    const shouldShowTertiary = unref(showTertiaryColumns)

    // actions列（固定列、オプション）
    if (includeActions) {
      const actionsHeader = {
        text: '',
        value: 'actions',
      }
      const actionsWithWidth = applyWidth(actionsHeader, COLUMN_WIDTHS.ACTIONS)
      headers.push(createFixedColumn(actionsWithWidth))
    }

    // 日付列（オプション）
    if (includeDate) {
      headers.push(
        applyWidth(
          {
            text: '日付',
            value: 'expenseDate',
          },
          COLUMN_WIDTHS.DATE,
        ),
      )
    }

    // 分類列のvalue名を決定
    const labelValuePrefix = useLabelIds
      ? 'expensePrimaryLabelId'
      : 'expensePrimaryLabelName'
    const secondaryValuePrefix = useLabelIds
      ? 'expenseSecondaryLabelId'
      : 'expenseSecondaryLabelName'
    const tertiaryValuePrefix = useLabelIds
      ? 'expenseTertiaryLabelId'
      : 'expenseTertiaryLabelName'
    const quaternaryValuePrefix = useLabelIds
      ? 'expenseQuaternaryLabelId'
      : 'expenseQuaternaryLabelName'

    // 主分類
    headers.push(
      applyWidth(
        {
          text: '主分類',
          value: labelValuePrefix,
        },
        COLUMN_WIDTHS.LABEL,
      ),
    )

    // 副分類
    headers.push(
      applyWidth(
        {
          text: '副分類',
          value: secondaryValuePrefix,
        },
        COLUMN_WIDTHS.LABEL,
      ),
    )

    // 条件付き分類列
    if (shouldShowTertiary) {
      headers.push(
        applyWidth(
          {
            text: '第三分類',
            value: tertiaryValuePrefix,
          },
          COLUMN_WIDTHS.LABEL,
        ),
      )

      headers.push(
        applyWidth(
          {
            text: '第四分類',
            value: quaternaryValuePrefix,
          },
          COLUMN_WIDTHS.LABEL,
        ),
      )
    }

    // その他の列
    headers.push(
      applyWidth(
        {
          text: '金額',
          value: 'amount',
        },
        COLUMN_WIDTHS.STANDARD,
      ),
    )

    // 税率列（ラベル名が異なる可能性がある）
    const taxRateText = useLabelIds ? '税率' : '消費税率'
    headers.push(
      applyWidth(
        {
          text: taxRateText,
          value: 'taxRate',
        },
        COLUMN_WIDTHS.STANDARD,
      ),
    )

    // 税額列
    const taxAmountText = useLabelIds ? '税額' : '消費税額'
    headers.push(
      applyWidth(
        {
          text: taxAmountText,
          value: 'taxAmount',
        },
        COLUMN_WIDTHS.STANDARD,
      ),
    )

    // 非現金列（value名が異なる）
    const nonCashValue = useLabelIds ? 'meta.isNonCash' : 'isNonCash'
    headers.push(
      applyWidth(
        {
          text: '非現金',
          value: nonCashValue,
        },
        COLUMN_WIDTHS.STANDARD,
      ),
    )

    return headers
  }

  // 6. computed headers
  const headers = computed(() => {
    return buildExpenseHeaders()
  })

  return {
    headers,
  }
}
